import { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

type Tone = "green" | "blue" | "amber" | "red" | "purple" | "slate";

const toneClasses: Record<Tone, { bg: string; text: string; soft: string }> = {
  green: { bg: "bg-[#009A44]", text: "text-[#009A44]", soft: "bg-[#009A44]/10" },
  blue: { bg: "bg-blue-600", text: "text-blue-600", soft: "bg-blue-50" },
  amber: { bg: "bg-amber-500", text: "text-amber-600", soft: "bg-amber-50" },
  red: { bg: "bg-red-500", text: "text-red-600", soft: "bg-red-50" },
  purple: { bg: "bg-purple-600", text: "text-purple-600", soft: "bg-purple-50" },
  slate: { bg: "bg-slate-600", text: "text-slate-600", soft: "bg-slate-100" },
};

export function PortalPageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-950">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function PortalActionButton({
  children,
  onClick,
  variant = "default",
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "default" | "outline";
}) {
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      className={cn(
        "h-9 rounded-md text-xs font-semibold",
        variant === "default" && "bg-[#009A44] text-white hover:bg-[#00843a]"
      )}
    >
      {children}
    </Button>
  );
}

export function RefreshButton({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
  return (
    <PortalActionButton variant="outline" onClick={onClick}>
      <RefreshCw className={cn("mr-2 h-4 w-4", loading && "animate-spin")} />
      Refresh
    </PortalActionButton>
  );
}

export function PortalStatCard({
  title,
  value,
  subtitle,
  icon,
  tone = "green",
  loading = false,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: ReactNode;
  tone?: Tone;
  loading?: boolean;
}) {
  const colors = toneClasses[tone];
  return (
    <Card className="rounded-lg border-slate-200 bg-white shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", colors.soft, colors.text)}>
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase text-slate-700">{title}</p>
            {loading ? (
              <Skeleton className="mt-2 h-7 w-28" />
            ) : (
              <p className="mt-2 text-xl font-bold tracking-tight text-slate-950">{value}</p>
            )}
            {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PortalPanel({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <Card className="rounded-lg border-slate-200 bg-white shadow-sm">
      <CardContent className="p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-bold text-slate-950">{title}</h2>
          {action}
        </div>
        <div className="p-4">{children}</div>
      </CardContent>
    </Card>
  );
}

export function SummaryLine({
  label,
  value,
  tone = "slate",
  money = false,
}: {
  label: string;
  value: string | number;
  tone?: Tone;
  money?: boolean;
}) {
  const colors = toneClasses[tone];
  return (
    <div className="flex items-center justify-between gap-3 py-2 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn("font-bold", colors.text)}>
        {money ? formatCurrency(Number(value || 0)) : value}
      </span>
    </div>
  );
}

export function PortalBadge({ children, tone = "green" }: { children: ReactNode; tone?: Tone }) {
  const colors = toneClasses[tone];
  return (
    <span className={cn("inline-flex rounded px-2 py-1 text-[11px] font-semibold", colors.soft, colors.text)}>
      {children}
    </span>
  );
}

export function EmptyRows({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-slate-500">
        {message}
      </td>
    </tr>
  );
}
